import React, { useRef, useState, useEffect, useCallback } from "react";

const DragScrollContainer = ({
  children,
  className = "",
  disabled = false,
  onScrollStart,
  onScrollEnd,
}) => {
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, scrollLeft: 0 });
  const [hasScrolled, setHasScrolled] = useState(false);

  const handleStart = (clientX, target) => {
    if (disabled || !containerRef.current) return;

    // フォーム要素はドラッグ開始しない
    if (target) {
      const tag = target.tagName && target.tagName.toLowerCase();
      if (["select", "input", "textarea", "button", "label"].includes(tag)) {
        return;
      }
    }

    // ドラッグ無効化エリア内かチェック
    if (target && target.closest(".no-drag-scroll")) {
      return;
    }

    setIsDragging(true);
    setHasScrolled(false);
    setStartPosition({
      x: clientX,
      scrollLeft: containerRef.current.scrollLeft,
    });

    // ドラッグ開始時にスクロール動作を無効化
    containerRef.current.style.scrollBehavior = "auto";

    onScrollStart?.();

    // ドラッグ中はテキスト選択を無効化
    document.body.style.userSelect = "none";
  };

  const handleMove = useCallback(
    (clientX) => {
      if (!isDragging || !containerRef.current) return;

      const deltaX = startPosition.x - clientX;
      const newScrollLeft = startPosition.scrollLeft + deltaX;

      // スクロール位置を直接設定（慣性なし）
      containerRef.current.style.scrollBehavior = "auto";
      containerRef.current.scrollLeft = newScrollLeft;

      // 移動距離が5px以上の場合、スクロールしたとみなす
      if (Math.abs(deltaX) > 5) {
        setHasScrolled(true);
      }
    },
    [isDragging, startPosition.x, startPosition.scrollLeft]
  );

  const handleEnd = () => {
    if (!isDragging) return;

    document.body.style.userSelect = "";

    // スクロール動作をスムーズに戻す
    if (containerRef.current) {
      containerRef.current.style.scrollBehavior = "smooth";
    }

    onScrollEnd?.();

    // 参考コードと同様の50ms遅延でドラッグ状態をリセット
    setTimeout(() => {
      setIsDragging(false);
      if (hasScrolled) {
        setHasScrolled(false);
      }
    }, 50);
  };

  // マウスイベント
  const handleMouseDown = (e) => {
    // フォーム要素は何もしない
    const tag = e.target.tagName && e.target.tagName.toLowerCase();
    if (["select", "input", "textarea", "button", "label"].includes(tag)) {
      return;
    }
    e.preventDefault();
    handleStart(e.clientX, e.target);
  };

  // タッチイベント
  const handleTouchStart = (e) => {
    // フォーム要素は何もしない
    const tag = e.target.tagName && e.target.tagName.toLowerCase();
    if (["select", "input", "textarea", "button", "label"].includes(tag)) {
      return;
    }
    handleStart(e.touches[0].clientX, e.target);
  };

  // グローバルイベントリスナー（マウスドラッグ用）
  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      handleMove(e.clientX);
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      handleEnd();
    };

    // より確実にイベントをキャッチ
    document.addEventListener("mousemove", handleMouseMove, {
      passive: false,
      capture: true,
    });
    document.addEventListener("mouseup", handleMouseUp, {
      passive: false,
      capture: true,
    });

    return () => {
      document.removeEventListener("mousemove", handleMouseMove, {
        capture: true,
      });
      document.removeEventListener("mouseup", handleMouseUp, { capture: true });
      // 念のためuser-selectをリセット
      document.body.style.userSelect = "";
    };
  }, [isDragging, handleMove]);

  // タッチ用 touchmove リスナー（passive: false で登録、preventDefault を有効化）
  // React の onTouchMove は React 17+ で passive listener になり preventDefault が効かないため、
  // native addEventListener で動的に登録する。ドラッグ中のみ有効化して縦スクロールを阻止。
  useEffect(() => {
    if (!isDragging) return;
    const container = containerRef.current;
    if (!container) return;

    const handleTouchMoveNative = (e) => {
      if (e.cancelable) e.preventDefault();
      if (e.touches && e.touches[0]) {
        handleMove(e.touches[0].clientX);
      }
    };

    container.addEventListener("touchmove", handleTouchMoveNative, {
      passive: false,
    });
    return () => {
      container.removeEventListener("touchmove", handleTouchMoveNative);
    };
  }, [isDragging, handleMove]);

  // クリックイベントを制御する関数
  const handleClick = useCallback(
    (e) => {
      // スクロールした場合のみクリックを無効化
      if (hasScrolled) {
        // フォーム要素はクリックを許可する
        const tag = e.target.tagName.toLowerCase();
        if (["select", "input", "textarea", "button", "label"].includes(tag)) {
          return;
        }
        // それ以外は無効化
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      // 通常のクリックは通す
    },
    [hasScrolled]
  );

  // 子要素にクリック制御情報を渡すためのプロバイダー
  const contextValue = {
    isClickDisabled: hasScrolled,
    isDragging,
  };

  return (
    <div
      ref={containerRef}
      className={`drag-scroll-container ${className} ${
        isDragging ? "dragging" : ""
      }`}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleEnd}
      onClick={handleClick}
      style={{
        cursor: isDragging ? "grabbing" : "grab",
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "thin",
        scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
      }}
      data-click-disabled={hasScrolled}
    >
      {children}
    </div>
  );
};

export default DragScrollContainer;
